import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { TransitionGroup } from 'react-transition-group';
// @mui
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  IconButton,
  Skeleton,
  InputAdornment,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Popover,
  MenuItem,
} from '@mui/material';
import { LoadingButton } from "@mui/lab";
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import SvgIconStyle from '../../components/SvgIconStyle';
import Iconify from '../../components/Iconify';
import Label from '../../components/Label';
import CompanyAvatar from '../company/CompanyAvatar';
// utils
import axios from '../../utils/axios';
import { mutate as swrMutate } from "swr";

export default function TimeOnDialog({ isOpen, closeDialog, trackingOn, onCompanyId=null }) {
  const smUp = useResponsive('up', 'sm');
  const router = useRouter();
  const { c } = router.query;
  const companyId = c || onCompanyId;

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true);
  const [step, setStep] = useState(0); // [0, 1] = [투두, 메모]
  const [upcomingData, setUpcomingData] = useState([]);

  // 투두에 추가or저장할 데이터
  const [todoContent, setTodoContent] = useState("");
  // 투두중에 삭제할 데이터
  const [toBeDelete, setToBeDelete] = useState([]);
  // 투두중에 공유할 데이터
  const [selectedTodo, setSelectedTodo] = useState([]);
  const [memo, setMemo] = useState("");

  // content dialog
  const [seeAllTodo, setSeeAllTodo] = useState(null);
  const [isOpenSeeAllContentDialog, setIsOpenSeeAllContentDialog] = useState(false);

  const submitTime = async () => {
    setIsSubmitting(true);
    await trackingOn({
      selects: selectedTodo || [],
      creates: upcomingData?.filter((d) => !('id' in d)) || [],
      removes: toBeDelete || [],
      memo: memo || '',
    })
    swrMutate('/api/works/time-tracking/crew/status/');
    setIsSubmitting(false);
  };

  const goPrev = () => {
    if (step === 0) {
      return ;
    }
    setStep(step - 1);
  };

  const nextStep = () => {
    if (step === 0 && selectedTodo?.length === 0) {
      // 투두가 없으면 메모 단계로
      setStep(1);
    } else if (step === 0 && selectedTodo?.length > 0) {
      // 투두가 있으면 바로 제출
      submitTime();
    } else if (step === 1 && memo.length > 0) {
      // 메모 입력 후 제출
      submitTime();
    } else {
      return ;
    }
  }

  const getTodoItems = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`/api/works/todo/crew/company/${companyId}/`);
      setUpcomingData(response.data?.todo || []);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
    }
  };

  const scrollToContentInput = async () => {
    const contentRef = document.getElementById('contentInput');
    contentRef.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  const enterToAdd = (e) => {
    if (e.key === "Enter" && todoContent) {
      addNewTodo();
      setTimeout(scrollToContentInput, 300);
    }
  };

  const addNewTodo = () => {
    if (todoContent) {
      setSelectedTodo([...selectedTodo, {content: todoContent}]);
      setTodoContent("");
    }
  };

  const addToDelete = (todo) => {
    setToBeDelete([...toBeDelete, todo]);
  };

  const openSeeAllContentDialog = (todo) => {
    setSeeAllTodo(todo);
    setIsOpenSeeAllContentDialog(true);
  };

  const selectTodo = (e, todo, index) => {
    e.stopPropagation();
    if (todo?.id) {
      setSelectedTodo([...selectedTodo, {content: todo?.content, id: todo?.id}]);
    } else {
      setSelectedTodo([...selectedTodo, {content: todo?.content}]);
    }
    upcomingData.splice(index, 1);
    setUpcomingData([...upcomingData]);
  };

  const unSelectTodo = (e, todo, index) => {
    e.stopPropagation();
    if (todo?.id) {
      setUpcomingData([...upcomingData, {content: todo?.content, id: todo?.id}]);
    } else {
      setUpcomingData([...upcomingData, {content: todo?.content}]);
    }
    selectedTodo.splice(index, 1);
    setSelectedTodo([...selectedTodo]);
  };

  const removeFromUpcoming = (todo, index) => {
    if (todo.id) {
      addToDelete(todo);
    }
    upcomingData.splice(index, 1);
    setUpcomingData([...upcomingData]);
  };

  const removeFromSelected = (todo, index) => {
    if (todo.id) {
      addToDelete(todo);
    }
    selectedTodo.splice(index, 1);
    setSelectedTodo([...selectedTodo]);
  };

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setIsSubmitting(false);
      setCanSubmit(true);
      setStep(0);
      setTodoContent("");
      setToBeDelete([]);
      setSelectedTodo([]);
      setSeeAllTodo(null);
      setIsOpenSeeAllContentDialog(false);
      setMemo("");
      getTodoItems();
    }
  }, [isOpen]);

  const handleMemoChange = (e) => {
    setMemo(e.target.value);
  };

  useEffect(() => {
    if (step === 1) {
      setCanSubmit(memo.length > 0);
    } else {
      setCanSubmit(true);
    }
  }, [step, memo]);

  return (
    <>
      <Dialog open={isOpen} onClose={closeDialog} fullWidth maxWidth="sm" fullScreen={!smUp}>
        <DialogContent sx={{ p: { xs: 3, sm: 4 }, pb: "0!important" }}>
          {/* header */}
          {step > 0 && (
          <Stack direction="row" sx={{ justifyContent: "space-between", mb: 1 }} alignItems="center">
            <IconButton color="inherit" edge="start" onClick={goPrev}>
              <Iconify icon="ic:chevron-left" width={32} height={32} />
            </IconButton>
            {/* padding */}
            <IconButton color="inherit" edge="end" onClick={closeDialog} sx={{ visibility: "hidden" }}>
              <Iconify icon="ic:close" width={24} height={24} />
            </IconButton>
          </Stack>
          )}

          {/* step 0 - 투두 선택 */}
          {step === 0 && (
          <>
            <Stack spacing={1} sx={{ mb: { xs: 2, sm: 3 } }}>
              <CompanyAvatar companyId={companyId} nameVariant="body2" width={20} height={20} sx={{ color: "text.secondary" }} />
              <Typography variant={smUp ? "h4" : "h5"}>
                오늘 진행할 업무를 알려주세요.
              </Typography>
              <Typography>
                선택된 항목은 회사에 공유됩니다 (선택된 항목: {selectedTodo?.length || "0"}개)
              </Typography>
            </Stack>
            {/* done todo */}
            {isLoading && (
              <Skeleton variant="rectangular" height={100}/>
            )}
            {!isLoading && selectedTodo?.length === 0 && upcomingData?.length === 0 && (
              <Box
                sx={{
                  my: 3,
                  p: 3,
                  border: "1px solid rgba(145, 158, 171, 0.24)",
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ py: 2, color: "text.disabled", textAlign: "center" }}>
                  데일리 투두가 비어있어요.
                </Typography>
              </Box>
            )}
            <TransitionGroup>
            {!isLoading && selectedTodo?.length > 0 && selectedTodo.filter((todo) => toBeDelete.findIndex((d) => d.id === todo.id) === -1).map((todo, index) => (
              <Collapse key={`selected-${index}`}>
                <TextField
                  fullWidth
                  value={selectedTodo[index].content}
                  multiline
                  maxRows={2}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="contained"
                          color="inherit"
                          sx={{
                            boxShadow: "none",
                            color: "text.disabled",
                            bgcolor: "action.disabledBackground"
                          }}
                          onClick={(e) => unSelectTodo(e, todo, index)}
                        >
                          선택 취소
                        </Button>
                        <MoreMenu
                          actions={
                            <>
                            <MenuItem onClick={() => removeFromSelected(todo, index)} sx={{ height: "46px" }}>
                              <Iconify icon="eva:trash-2-outline" sx={{ mr: 1.5, width: 18, height: 18 }} />
                              투두 삭제하기
                            </MenuItem>
                            <MenuItem onClick={() => openSeeAllContentDialog(todo)} sx={{ height: "46px" }}>
                              <SvgIconStyle src={`/icons/icon-eye-on-mono.svg`} sx={{ mr: 1.5, width: 18, height: 18 }} />
                              내용 전체보기
                            </MenuItem>
                            </>
                          }
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      overflow: "hidden",
                      textOverflow: "ellipsis", 
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      fontWeight: 700,
                    },
                    ':hover': {
                      cursor: 'pointer',
                    },
                    mb: 1.5,
                  }}
                />
              </Collapse>
            ))}
            {!isLoading && upcomingData?.length > 0 && upcomingData.filter((todo) => toBeDelete.findIndex((d) => d.id === todo.id) === -1).map((todo, index) => (
              <Collapse key={`upcoming-${index}`}>
                <TextField
                  fullWidth
                  value={upcomingData[index].content}
                  multiline
                  maxRows={2}
                  sx={{
                    "& .MuiInputBase-input": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      fontWeight: 700,
                    },
                    ':hover': {
                      cursor: 'pointer',
                    },
                    mb: 1.5,
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="contained"
                          color="primary"
                          sx={{
                            boxShadow: "none",
                          }}
                          onClick={(e) => selectTodo(e, todo, index)}
                        >
                          선택하기
                        </Button>
                        <MoreMenu
                          actions={
                            <>
                            <MenuItem onClick={() => removeFromUpcoming(todo, index)} sx={{ height: "46px" }}>
                              <Iconify icon="eva:trash-2-outline" sx={{ mr: 1.5, width: 18, height: 18 }} />
                              투두 삭제하기
                            </MenuItem>
                            <MenuItem onClick={() => openSeeAllContentDialog(todo)} sx={{ height: "46px" }}>
                              <SvgIconStyle src={`/icons/icon-eye-on-mono.svg`} sx={{ mr: 1.5, width: 18, height: 18 }} />
                              내용 전체보기
                            </MenuItem>
                            </>
                          }
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Collapse>
            ))}
            </TransitionGroup>
            {!isLoading && (
              <Stack
                sx={{
                  p: 3,
                  bgcolor: "action.selected",
                  borderRadius: 2,
                }}
                spacing={2}
              >
                <Stack
                  direction="row" alignItems="center" spacing={1}
                >
                  <SvgIconStyle src={`/icons/icon-plus-circle-mono.svg`} sx={{ width: 24, height: 24, color: "info.main" }} />
                  <Typography variant="h6">
                    오늘 진행할 업무를 추가해보세요.
                  </Typography>
                </Stack>
                <TextField
                  id={"contentInput"}
                  fullWidth
                  value={todoContent}
                  onChange={(e) => setTodoContent(e.target.value)}
                  onKeyUp={enterToAdd}
                  inputProps={{ maxLength: 500 }}
                  sx={{
                    borderRadius: 1,
                    bgcolor: "background.paper",
                    ":hover": {
                      cursor: "text",
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant={todoContent ? "contained" : "outlined"}
                          disabled={!todoContent}
                          onClick={addNewTodo}
                        >
                          추가
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ width: 1, textAlign: "end" }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {todoContent?.length === 0 ? "500자 이내" : `${todoContent?.length}/500`}
                  </Typography>
                </Box>
              </Stack>
            )}
          </>
          )}
          
          {/* step 1 - 메모 입력 */}
          {step === 1 && (
          <>
            <CompanyAvatar companyId={companyId} nameVariant="body2" width={20} height={20} sx={{ color: "text.secondary", mb: 1 }} />
            <Typography variant={smUp ? "h4" : "h5"} sx={{ mb: 3 }}>
              오늘 진행할 업무를 선택하지 않으셨습니다. <br/>
              어떤 상황인지 알려주세요!
            </Typography>
            <TextField
              id={"workStartMemo"}
              fullWidth
              placeholder={"만약 회사와 따로 소통하고 있다면, DIO의 업무 모니터링을 위해 해당 내용을 붙여 넣어주세요."}
              multiline
              rows={8}
              value={memo}
              onChange={handleMemoChange}
            />
            <Box sx={{ mt: 2, width: 1, p: 2, bgcolor: "grey.200", borderRadius: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems={"center"} sx={{ mb: 1 }}>
                <Label color="info" variant="filled">
                  예시
                </Label>
                <Typography variant="subtitle2">
                  상황을 구체적으로 작성해주세요.
                </Typography>
              </Stack>
              <List sx={{ listStyle: "disc", pl: 2.5 }} dense disablePadding>
                <ListItem sx={{ p: 0, display: "list-item" }}>
                  <ListItemText
                    primary={"이번주에 진행할 업무를 아직 전달받지 못했어요."}
                    primaryTypographyProps={{ fontSize: (theme) => theme.typography.caption.fontSize }}
                  />
                </ListItem>
                <ListItem sx={{ p: 0, display: "list-item" }}>
                  <ListItemText
                    primary={"오늘 출근 후에 회사와 앞으로 해야할 업무를 정하기로 협의했어요."}
                    primaryTypographyProps={{ fontSize: (theme) => theme.typography.caption.fontSize }}
                  />
                </ListItem>
              </List>
            </Box>
          </>
          )}
        </DialogContent>
        <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
          <Stack sx={{ width: 1 }} spacing={{ xs: 2, sm: 1 }}>
            <Stack
              justifyContent={"flex-end"}
              direction={smUp ? "row" : "column-reverse"}
              sx={{ width: 1 }}
              spacing={{ xs: 1, sm: 3 }}
            >
              <LoadingButton
                loading={isSubmitting}
                variant="text"
                onClick={closeDialog}
                size="large"
                sx={{ px: 6 }}
                fullWidth={!smUp}
              >
                취소
              </LoadingButton>
              <LoadingButton
                loading={isSubmitting}
                variant="contained"
                onClick={nextStep}
                size="large"
                sx={{ px: 4 }}
                fullWidth={!smUp}
                disabled={!canSubmit}
              >
                출근 기록 시작하기
              </LoadingButton>
            </Stack>
          </Stack>
        </DialogActions>
        <SeeAllTodoContentDialog todo={seeAllTodo} open={isOpenSeeAllContentDialog} closeDialog={() => setIsOpenSeeAllContentDialog(false)}/>
      </Dialog>
    </>
  )
};


function MoreMenu({ actions }) {
  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  return (
    <>
      <IconButton onClick={handleOpen} sx={{ ...open&&{bgcolor: "action.hover" }, borderRadius: 1 }}>
        <SvgIconStyle src={`/icons/icon-dots-mono.svg`} sx={{ width: 20, height: 20, color: "text.disabled" }} />
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            overflow: 'inherit',
            width: 150,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'subtitle2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        {actions}
      </Popover>
    </>
  );
};

function SeeAllTodoContentDialog({ todo, open, closeDialog }) {
  return (
    <Dialog open={open} fullWidth maxWidth="dg">
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack sx={{ pb: 1 }} direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <SvgIconStyle src={`/icons/icon-eye-on-mono.svg`} sx={{ color: "text.disabled", width: 20, height: 20 }} />
            <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
              내용 전체보기
            </Typography>
          </Stack>
          <IconButton onClick={closeDialog} size="small" edge="end">
            <Iconify icon={'ic:close'} sx={{ color: "text.secondary", width: '24px', height: '24px' }} />
          </IconButton>
        </Stack>
        <Typography variant="subtitle1" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {todo?.content}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};